import { useEffect, useState } from 'react';
import { getHealth } from '@/api/client';
import { useAmbientTranscript, type AmbientTranscriptHook } from './useAmbientTranscript';
import { useWhisperTranscript } from './useWhisperTranscript';

/**
 * Picks which ambient-transcript engine backs voice capture: server-side
 * Whisper when the API reports it configured (better accuracy on
 * radio-quality audio and jargon, and works in any browser — not just
 * Chrome/Edge), otherwise the on-device Web Speech API fallback, so voice
 * capture still works with AI disabled/unconfigured — the same
 * deterministic-path-always-works contract as the rest of the app's AI
 * features.
 *
 * Both underlying hooks are always mounted (cheap — neither does anything
 * until `start()` is called) so only the chosen one's `start`/`stop` is
 * ever invoked by the caller; there's never dual capture.
 */
export function useTranscriptSource(): AmbientTranscriptHook {
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getHealth()
      .then((h) => {
        if (!cancelled) setTranscriptionEnabled(h.transcriptionEnabled);
      })
      .catch(() => {
        // Health check failed — stay on the always-available Web Speech fallback.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const whisper = useWhisperTranscript(transcriptionEnabled);
  const webSpeech = useAmbientTranscript();

  return transcriptionEnabled ? whisper : webSpeech;
}
